import { clearSharedPlaybackCache, isSharedPlayback, openLibraryAudio, releaseLibraryAudio, revalidateLibraryAudio } from '../api/libraryPlayback';
import { useSharedPlaybackAuthority } from '../hooks/useSharedPlaybackAuthority';
import React, { createContext, useCallback, useContext, useEffect, useLayoutEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { RenderDetail, fetchRender } from '../api/rendersApi';
import { ApiError, isAbortError, toApiError } from '../api/http';
import { guardAudioLease } from '../api/audioLease';
import { getKnownCacheEpoch } from '../api/cacheEpochApi';
import { isOfflineCacheCurrent } from '../api/offlineCache';
import { useApp } from './AppContext';
import { useBedtimeContext } from './BedtimeContext';
import { Player } from '../components/ui';

interface ParentPreviewValue {
  activeRender: RenderDetail | null;
  loadingRenderId: string | null;
  isPlaying: boolean;
  currentS: number;
  durationS: number;
  playError: ApiError | null;
  playErrorRenderId: string | null;
  handlePlay: (renderId: string) => Promise<void>;
  seek: (seconds: number) => void;
  stop: () => void;
}

const ParentPreviewContext = createContext<ParentPreviewValue | null>(null);
export const useParentPreview = () => useContext(ParentPreviewContext);

/** One authorized preview owner for parent routes. Review and child audio stay separate. */
export const ParentPreviewProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const { state } = useApp();
  const context = useBedtimeContext();
  const pair = context.pair;
  const { pathname } = useLocation();
  const reviewOpen = /^\/renders\/[^/]+\/?$/.test(pathname) || /^\/admin\/library\/[^/]+\/sharing\/?$/.test(pathname);
  const identity = JSON.stringify([state.user?.id, pair?.householdId, pair?.listenerId, pair?.voiceId]);
  const audioRef = useRef<HTMLAudioElement | null>(null);
  const requestRef = useRef(0);
  const controllerRef = useRef<AbortController | null>(null);
  const [activeRender, setActiveRender] = useState<RenderDetail | null>(null);
  const [loadingRenderId, setLoadingRenderId] = useState<string | null>(null);
  const [isPlaying, setIsPlaying] = useState(false);
  const [currentS, setCurrentS] = useState(0);
  const [durationS, setDurationS] = useState(0);
  const [playError, setPlayError] = useState<ApiError | null>(null);
  const [playErrorRenderId, setPlayErrorRenderId] = useState<string | null>(null);
  const [networkOnline, setOnline] = useState(() => navigator.onLine);
  const online = networkOnline && !state.offlineSession;

  const releaseAudio = useCallback(() => {
    const audio = audioRef.current;
    if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
  }, []);
  const bindAudio = useCallback((node: HTMLAudioElement | null) => {
    if (audioRef.current && audioRef.current !== node) releaseAudio();
    audioRef.current = node;
  }, [releaseAudio]);
  const stop = useCallback(() => {
    requestRef.current += 1;
    controllerRef.current?.abort();
    releaseAudio();
    setActiveRender(null); setLoadingRenderId(null); setIsPlaying(false);
    setCurrentS(0); setDurationS(0); setPlayError(null); setPlayErrorRenderId(null);
  }, [releaseAudio]);

  useSharedPlaybackAuthority(activeRender, audioRef, stop);
  useEffect(() => () => releaseLibraryAudio(activeRender), [activeRender]);

  // Invalidate pending fetches and buffered media before a new route/identity can play.
  useLayoutEffect(() => { stop(); clearSharedPlaybackCache(); }, [identity, stop]);
  useLayoutEffect(() => { stop(); }, [reviewOpen, stop]);
  useLayoutEffect(() => {
    const audio = audioRef.current;
    return () => {
      requestRef.current += 1;
      controllerRef.current?.abort();
      if (audio) { audio.pause(); audio.removeAttribute('src'); audio.load(); }
    };
  }, []);
  useEffect(() => {
    const onOnline = () => setOnline(true);
    const onOffline = () => setOnline(false);
    window.addEventListener('online', onOnline);
    window.addEventListener('offline', onOffline);
    // Also covers invalidation while a render request is still opening.
    window.addEventListener('luma-access-invalidated', stop);
    return () => {
      window.removeEventListener('online', onOnline);
      window.removeEventListener('offline', onOffline);
      window.removeEventListener('luma-access-invalidated', stop);
    };
  }, [stop]);

  useEffect(() => {
    const voice = context.voices.find((item) => item.voice_id === pair?.voiceId);
    if (context.status === 'no_ready_voice' || (voice && (voice.consent_active === false || voice.status !== 'ready'))) stop();
  }, [context.status, context.voices, pair?.voiceId, stop]);

  const offlineAllowed = useCallback((renderId: string) => Boolean(pair && isOfflineCacheCurrent(
    state.user?.id || '', renderId, pair.householdId, pair.listenerId, pair.voiceId, getKnownCacheEpoch(pair.householdId)
  )), [pair, state.user?.id]);
  const matchesPair = useCallback((detail: RenderDetail) => Boolean(pair && detail.status === 'parent_approved'
    && detail.household_id === pair.householdId && detail.listener_id === pair.listenerId && detail.voice_id === pair.voiceId), [pair]);

  const handlePlay = useCallback(async (renderId: string) => {
    if (!pair || !state.user || reviewOpen) return;
    if (!online && !offlineAllowed(renderId)) {
      stop();
      setPlayError(toApiError(new Error('Offline authorization expired'), 'Connect once to refresh this saved bedtime.'));
      setPlayErrorRenderId(renderId);
      return;
    }
    if (activeRender?.render_id === renderId && audioRef.current && !playError) {
      if (audioRef.current.paused) audioRef.current.play().catch(() => setIsPlaying(false));
      else audioRef.current.pause();
      return;
    }
    stop(); // Replacement stops the previous media even if opening the new one fails.
    const requestId = ++requestRef.current;
    const controller = new AbortController();
    controllerRef.current = controller;
    setLoadingRenderId(renderId);
    try {
      const detail = await openLibraryAudio(renderId, controller.signal);
      if (requestId !== requestRef.current || controller.signal.aborted) { releaseLibraryAudio(detail); return; }
      if (!matchesPair(detail) || !detail.audio_url) { releaseLibraryAudio(detail); throw new Error('not available'); }
      setActiveRender(detail);
    } catch (error) {
      if (!isAbortError(error) && requestId === requestRef.current) {
        setPlayError(toApiError(error, 'This saved story could not be opened.'));
        setPlayErrorRenderId(renderId);
      }
    } finally { if (requestId === requestRef.current) setLoadingRenderId(null); }
  }, [pair, state.user, reviewOpen, online, offlineAllowed, activeRender, playError, matchesPair, stop]);

  useEffect(() => {
    const audio = audioRef.current;
    if (!audio || !activeRender || playError || isSharedPlayback(activeRender.render_id)) return;
    return guardAudioLease(audio, activeRender.offline_access_expires_at, stop);
  }, [activeRender, playError, stop]);

  useEffect(() => {
    if (!activeRender) return;
    const controller = new AbortController();
    const requestId = requestRef.current;
    const revalidate = async () => {
      audioRef.current?.pause();
      try {
        if (isSharedPlayback(activeRender.render_id)) {
          if (!await revalidateLibraryAudio(activeRender, controller.signal) && !controller.signal.aborted) stop();
          return;
        }
        const detail = await fetchRender(activeRender.render_id, true, controller.signal);
        if (controller.signal.aborted || requestRef.current !== requestId) return;
        // Never continue a revoked, unapproved, or replaced source on reconnect.
        if (!matchesPair(detail) || detail.audio_url !== activeRender.audio_url) { stop(); return; }
        setActiveRender(detail);
      } catch { if (!controller.signal.aborted && requestRef.current === requestId) stop(); }
    };
    window.addEventListener('online', revalidate);
    return () => { controller.abort(); window.removeEventListener('online', revalidate); };
  }, [activeRender, matchesPair, stop]);

  useEffect(() => {
    if (activeRender && !playError && audioRef.current?.getAttribute('src')) {
      audioRef.current.play().catch(() => setIsPlaying(false));
    }
  }, [activeRender, playError]);

  const seek = (seconds: number) => {
    if (audioRef.current && activeRender && !playError) {
      audioRef.current.currentTime = seconds;
      setCurrentS(seconds);
    }
  };
  return <ParentPreviewContext.Provider value={{ activeRender, loadingRenderId, isPlaying, currentS, durationS, playError, playErrorRenderId, handlePlay, seek, stop }}>
    {children}
    {activeRender && <audio ref={bindAudio} data-testid="parent-preview-audio" src={activeRender && !playError ? activeRender.audio_url || undefined : undefined} preload="metadata"
      onPlay={() => setIsPlaying(true)} onPause={() => setIsPlaying(false)} onEnded={() => setIsPlaying(false)}
      onLoadedMetadata={(event) => setDurationS(event.currentTarget.duration)}
      onTimeUpdate={(event) => {
        if (!activeRender) return;
        const expired = activeRender.offline_access_expires_at != null && !(Date.now() < Date.parse(activeRender.offline_access_expires_at));
        if (expired || (!online && !offlineAllowed(activeRender.render_id))) { stop(); return; }
        setCurrentS(event.currentTarget.currentTime);
      }}
      onError={() => {
        if (!activeRender || playError) return;
        releaseAudio(); setIsPlaying(false);
        setPlayError(toApiError(new Error('Source failed'), 'This saved story could not be opened.'));
        setPlayErrorRenderId(activeRender.render_id);
      }} />}
  </ParentPreviewContext.Provider>;
};

export const ParentPreviewDock: React.FC = () => {
  const preview = useParentPreview();
  const { state } = useApp();
  const bedtime = useBedtimeContext();
  if (!preview?.activeRender) return null;
  const { activeRender, isPlaying, currentS, durationS, playError, handlePlay, seek, stop } = preview;
  const story = state.stories.find((item) => item.story_id === activeRender.story_id);
  const voice = bedtime.voices.find((item) => item.voice_id === activeRender.voice_id);
  return <Player mode="parentPreview" title={story?.title || activeRender.story_title || activeRender.story_id || 'Saved bedtime story'}
    subtitle={voice?.voice_name || 'Family voice'} artwork={story ? { src: story.thumbnail_url, srcSet: story.thumbnail_srcset } : null}
    playing={isPlaying} currentTime={currentS} duration={durationS || activeRender.output_seconds || 0}
    error={playError ? 'This saved story could not be opened.' : null}
    onPlay={() => void handlePlay(activeRender.render_id)} onPause={() => void handlePlay(activeRender.render_id)}
    onSeek={playError ? undefined : seek} onClose={stop} />;
};
